// FIXME: Non-DRY overlap with EvalDepsContext?
//
// EvalContext — the engine's dependency-injection surface.
//
// Every `evalExpr` call receives exactly one `EvalContext`. The shape
// is deliberately small and *hybrid*:
//
//   - plain values for per-call configuration (the `session` and the
//     optional `signal`).
//
// `Session` carries conversation-scoped state (I/O memoization +
// dataset bindings between turns); see `session/Session.ts`. The
// `AbortSignal` is optional; when present, I/O ops cancel implicitly
// through `session.fetch` — op authors do not check `signal.aborted`
// directly.
//
// The engine does *not* memoize callback results. Callers are
// responsible for callback stability (closing over refs that don't
// change unnecessarily). The engine's contract is "call when needed";
// no caching layer is interposed.

import type { DatasetProvider } from './providers/types.ts';
import type { ProviderResolver } from './providers/useProviderRegistry.ts';
import { EMPTY_IDENTITY_TABLE, type IdentityTable } from './schema/row-identity.ts';
import { type BindingResolver, createMutableBindingResolver } from './session/bindingResolver.ts';
import { createInMemorySession } from './session/createInMemorySession.ts';
import type { Session } from './session/Session.ts';

/**
 * Context handed to a provider's `fetch`. Carries the session (for
 * downstream `session.fetch` calls inside the provider if it composes
 * smaller reads) and the optional cancellation signal from the
 * surrounding `EvalContext`.
 *
 * Kept narrow — a provider's `fetch` must not receive the full
 * `EvalContext` because it must not recurse back into expression
 * evaluation.
 */
export type SourceFetcherCtx = {
  readonly session: Session;
  readonly signal?: AbortSignal;
};

/**
 * Engine DI surface. One per `evalExpr` invocation.
 *
 * `useEvalExpr()` constructs an `EvalContext` from React state and
 * passes it through.
 */
export type EvalContext = {
  /**
   * Conversation-scoped session. I/O ops route memoizable reads
   * through `session.fetch(key, fetcher)`. The chat plugin supplies a
   * TanStack-Query-backed impl; non-React callers use
   * `createInMemorySession()`.
   */
  readonly session: Session;

  /**
   * Cross-turn dataset binding resolver. A pure, transcript-derived
   * snapshot of the producer recipes visible at the current position;
   * the evaluator's `symbolRef` fallthrough and `(col …)` resolution
   * route name lookups through `bindings.resolve` / `bindings.has`
   * instead of any mutable store. `defaultEvalCtx()` supplies an empty
   * resolver; the chat layer derives one per tool-call position.
   */
  readonly bindings: BindingResolver;

  /**
   * Optional cancellation signal. Plumbed into `session.fetch`
   * implementations that support it (e.g. TanStack `fetchQuery`).
   * Ops do not consume the signal directly.
   */
  readonly signal?: AbortSignal;

  /**
   * Dataset-provider registry resolver. Resolves a `(group, name)` to
   * the first provider whose `claims(name)` is true. Threaded onto BOTH
   * the runtime context (fetch dispatch by `providerId`) and the sync
   * type-check pass (`SourceSchemaCtx.getProvider`, wired in
   * `evalScript`). This is the single dataset-resolution surface — the
   * CDF view provider captures its views at registry-build time, so the
   * engine never needs a separate views accessor.
   *
   * Optional — `defaultEvalCtx()` leaves this `undefined`; source ops
   * then degrade to the untracked-binding fallback in the static pass.
   */
  readonly getProvider?: ProviderResolver;

  /**
   * Provider-registry resolver by provider `id`, used by the materialiser
   * to dispatch a `ProviderSource` to its owning provider's `fetch`. A
   * `ProviderSource` carries only `providerId` (not the group + name it
   * was minted from), so fetch dispatch resolves through this rather than
   * `getProvider`. Same registry as `getProvider`; both reach the engine
   * from the one snapshot the host builds per `(client, views)`.
   *
   * Optional — absent ⇒ a `ProviderSource` plan surfaces a
   * `MaterializationError('missing-provider', …)` at materialisation time.
   */
  readonly getProviderById?: (id: string) => DatasetProvider | undefined;

  /**
   * Composed view over every provider-contributed row-identity contract.
   * Threaded (alongside the op `NameTable`) into the type-check, compile,
   * and eval passes so identity recognition, equality, dedup keying, and
   * scoping are answered through one provider-agnostic surface rather than
   * any hard-coded structural sniff. Built at the provider/session
   * boundary from the registry's identity contributions.
   *
   * Optional — `defaultEvalCtx()` and callers without provider-contributed
   * identities leave this `undefined`; the engine then falls back to
   * `EMPTY_IDENTITY_TABLE`, which claims nothing (a benign no-op).
   */
  readonly identities?: IdentityTable;
};

/**
 * Construct a default `EvalContext` backed by a fresh in-memory
 * session. Used as:
 *
 *   - the `evalExpr` / `evalScript` parameter default — so test
 *     callers stay terse;
 *   - tests that need to *share* a session across multiple
 *     evaluations (so cross-call bindings or memoized fetches
 *     persist between invocations within one test).
 *
 * Production callers should construct their own `EvalContext` from
 * React state via `useEvalExpr()` — that path supplies a
 * TanStack-Query-backed session
 */
export const defaultEvalCtx = (): EvalContext => ({
  session: createInMemorySession(),
  // Mutable (not empty) so the documented "share a session across
  // evaluations, cross-call bindings persist" contract holds for
  // REPL/CLI/test callers. Production builds its ctx via
  // `useEvalExpr()` with a transcript-derived resolver.
  bindings: createMutableBindingResolver(),
  // No provider has contributed identity contracts in the default
  // in-memory context; the empty table claims nothing.
  identities: EMPTY_IDENTITY_TABLE,
});
